import { ChangeEvent, FC, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import {
  BottomSheet,
  Button,
  Icon,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { EmployeePermission } from 'src/api/permissions';
import { BookingObjectTypeSelect } from 'src/components/BookingObjectTypeSelect/BookingObjectTypeSelect';
import { BookingObjectType } from 'src/components/BookingObjectTypeSelect/types';
import {
  BuildingSelect,
  BuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import {
  FloorSelect,
  FloorSelectItem,
} from 'src/components/FloorSelect/FloorSelect';
import { Section } from 'src/components/Section/Section';
import {
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { UploadButton } from 'src/components/UploadButton/UploadButton';
import { useHost } from 'src/providers/HostProvider';
import {
  getAdminFloorSelectItem,
  getAdminTownSelectItem,
  getAdminBuildingSelectItem,
  getAdminState,
} from 'src/store/admin/selectors';
import { uploadPlan } from 'src/store/admin/services';
import { adminActions } from 'src/store/admin/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getUserPermissions } from 'src/store/user/selectors';
import { noop } from 'src/utils/noop';

import { AdministratorUserPermissionsButton } from '../AdministratorUserPermissions/AdministratorUserPermissions';
import {
  parkingBookingObjectTypeOptions,
  regularBookingObjectTypeOptions,
} from '../consts';

import classes from './AdminSettingsBottomSheet.module.scss';

const Inner: FC = () => {
  const floor = useSelector(getAdminFloorSelectItem);
  const town = useSelector(getAdminTownSelectItem);
  const building = useSelector(getAdminBuildingSelectItem);

  const data = useSelector(getUserPermissions);

  const { backgroundData, selectedPlace, bookingObjectType } =
    useSelector(getAdminState);

  const dispatch = useAppDispatch();

  const handleTownChange = useCallback(
    (nextTown: TownSelectItem | undefined) => {
      dispatch(adminActions.setTownId(nextTown?.value.townId));
    },
    [dispatch],
  );

  const handleBuildingChange = useCallback(
    (nextBuilding: BuildingSelectItem | undefined) => {
      dispatch(adminActions.setBuildingId(nextBuilding?.value.buildingId));
    },
    [dispatch],
  );

  const handleFloorChange = useCallback(
    (nextFloor: FloorSelectItem | undefined) => {
      dispatch(adminActions.setFloorId(nextFloor?.value.floorId));
    },
    [dispatch],
  );

  const createTown = useCallback(() => {
    dispatch(adminActions.setSelectedTownId(undefined));
    dispatch(adminActions.openTown());
  }, [dispatch]);

  const createBuilding = useCallback(() => {
    dispatch(adminActions.setSelectedBuildingId(undefined));
    dispatch(adminActions.openBuilding());
  }, [dispatch]);

  const createFloor = useCallback(() => {
    dispatch(adminActions.setSelectedFloorId(undefined));
    dispatch(adminActions.openFloor());
  }, [dispatch]);

  const editTown = useCallback(() => {
    dispatch(adminActions.setSelectedTownId(town?.value.townId));
    dispatch(adminActions.openTown());
  }, [dispatch, town?.value.townId]);

  const editBuilding = useCallback(() => {
    dispatch(adminActions.setSelectedBuildingId(building?.value.buildingId));
    dispatch(adminActions.openBuilding());
  }, [building?.value.buildingId, dispatch]);

  const editFloor = useCallback(() => {
    dispatch(adminActions.setSelectedFloorId(floor?.value.floorId));
    dispatch(adminActions.openFloor());
  }, [dispatch, floor?.value.floorId]);

  const upload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      await dispatch(uploadPlan(file));
    },
    [dispatch],
  );

  const setBookingObjectType = useCallback(
    (value: BookingObjectType) => {
      dispatch(adminActions.setBookingObjectType(value));
    },
    [dispatch],
  );

  return (
    <BottomSheet
      isOpen
      handleSetOpen={noop}
      swiper
      modal={false}
      snapPoints={[70, 0.5, 1]}
      style={{ minHeight: '70px' }}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Settings,
        title: 'Настройки',
      }}
    >
      <div className={classes.container}>
        <Section title='Настройки плана'>
          <div className={classes.fieldContainer}>
            <div className={classes.field}>
              <TownSelect
                town={town}
                onChange={handleTownChange}
                dataTestId='administratorTown'
              />
            </div>
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Edit} />}
              onClick={editTown}
              disabled={!town}
              className={classes.button}
            />
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Add} />}
              onClick={createTown}
              className={classes.button}
            />
          </div>

          <div className={classes.fieldContainer}>
            <div className={classes.field}>
              <BuildingSelect
                building={building}
                onChange={handleBuildingChange}
                townId={town?.value.townId}
                disabled={!town}
                dataTestId='administratorBuilding'
                key={town?.value.townId}
              />
            </div>
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Edit} />}
              onClick={editBuilding}
              disabled={!building}
              className={classes.button}
            />
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Add} />}
              onClick={createBuilding}
              className={classes.button}
            />
          </div>

          <div className={classes.fieldContainer}>
            <div className={classes.field}>
              <FloorSelect
                floor={floor}
                onChange={handleFloorChange}
                buildingId={building?.value.buildingId}
                disabled={!building}
                dataTestId='administratorFloor'
                key={building?.value.buildingId}
              />
            </div>
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Edit} />}
              onClick={editFloor}
              disabled={!floor}
              className={classes.button}
            />
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Add} />}
              onClick={createFloor}
              className={classes.button}
            />
          </div>
          <UploadButton
            onUpload={upload}
            variant='outlined'
            size='medium'
            disabled={!floor}
          >
            Загрузить план
          </UploadButton>
        </Section>
        <Section title='Объект для добавления'>
          <BookingObjectTypeSelect
            options={
              floor?.value.isParking
                ? parkingBookingObjectTypeOptions
                : regularBookingObjectTypeOptions
            }
            bookingObjectType={bookingObjectType}
            setBookingObjectType={setBookingObjectType}
            disabled={!backgroundData || !!selectedPlace}
          />
        </Section>
        {data?.permissions.has(EmployeePermission.SystemAdmin) && (
          <Section title='Настройки прав пользователей'>
            <AdministratorUserPermissionsButton>
              Редактировать права
            </AdministratorUserPermissionsButton>
          </Section>
        )}
      </div>
    </BottomSheet>
  );
};

export const AdminSettingsBottomSheet: FC = () => {
  const { host } = useHost();

  return host ? createPortal(<Inner />, host) : null;
};
